import { STORAGE } from '../constants'
import type { Settings } from '../types'
import { isSyncSnapshot } from './remoteProtocol'
import { applySyncSnapshot, createSyncSnapshot, SYNC_FILE_MAX_BYTES, type SyncSnapshot } from './sync'

export interface HouseholdProfile {
  id: string
  name: string
  active: boolean
  snapshot?: SyncSnapshot
}

function readProfiles(): HouseholdProfile[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE.householdProfiles) || '[]') as unknown
    if (!Array.isArray(value)) return []
    const seen = new Set<string>()
    const cleaned = value.flatMap((entry): HouseholdProfile[] => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
      const raw = entry as Record<string, unknown>
      if (typeof raw.id !== 'string' || !raw.id || raw.id.length > 80 || seen.has(raw.id)
        || typeof raw.name !== 'string' || !raw.name.trim() || raw.name.length > 24
        || typeof raw.active !== 'boolean') return []
      if (raw.snapshot !== undefined && (!isSyncSnapshot(raw.snapshot, 100)
        || new TextEncoder().encode(JSON.stringify(raw.snapshot)).byteLength > SYNC_FILE_MAX_BYTES)) return []
      seen.add(raw.id)
      return [{ id: raw.id, name: raw.name.trim(), active: raw.active, snapshot: raw.snapshot as SyncSnapshot | undefined }]
    }).slice(0, 6)
    const activeIndex = cleaned.findIndex((entry) => entry.active)
    return cleaned.map((entry, index, entries) => ({
      ...entry,
      active: index === (activeIndex >= 0 ? activeIndex : 0) && entries.length > 0,
    }))
  } catch { return [] }
}

function writeProfiles(value: HouseholdProfile[]) {
  localStorage.setItem(STORAGE.householdProfiles, JSON.stringify(value.slice(0, 6)))
}

export function getHouseholdProfiles() {
  const profiles = readProfiles()
  if (profiles.length) return profiles
  const initial: HouseholdProfile[] = [{ id: 'main', name: 'Main', active: true }]
  writeProfiles(initial)
  return initial
}

export function createHouseholdProfile(name: string, settings: Settings) {
  const current = getHouseholdProfiles()
  if (current.length >= 6) throw new Error('Vela supports up to six household profiles.')
  const id = `profile-${Date.now().toString(36)}`
  const next = current.map((entry) => entry.active ? { ...entry, snapshot: createSyncSnapshot(settings) } : entry)
  next.push({ id, name: name.trim().slice(0, 24) || `Profile ${next.length + 1}`, active: false, snapshot: { ...createSyncSnapshot(settings), library: [], progress: [], watched: {} } })
  writeProfiles(next)
  return next
}

export function switchHouseholdProfile(id: string, settings: Settings) {
  const current = getHouseholdProfiles()
  const target = current.find((entry) => entry.id === id)
  if (!target) throw new Error('Profile no longer exists.')
  const saved = current.map((entry) => entry.active ? { ...entry, snapshot: createSyncSnapshot(settings), active: false } : { ...entry, active: entry.id === id })
  writeProfiles(saved)
  if (target.snapshot) applySyncSnapshot(JSON.stringify(target.snapshot), settings, 'replace')
  else applySyncSnapshot(JSON.stringify({ ...createSyncSnapshot(settings), library: [], progress: [], watched: {} }), settings, 'replace')
  return saved
}

export function deleteHouseholdProfile(id: string) {
  const profiles = getHouseholdProfiles()
  const target = profiles.find((entry) => entry.id === id)
  if (!target || target.active || profiles.length === 1) throw new Error('Switch away before deleting this profile.')
  const next = profiles.filter((entry) => entry.id !== id)
  writeProfiles(next)
  return next
}
